import { Component, OnInit, ViewChild } from '@angular/core';
import { KuiModalRefComponent } from 'app/key-ui/modal/modal-ref.component';
import { IKuiModalAction } from 'app/key-ui';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from 'app/app.service';
import { ModalService } from 'app/shared/components/modal';
import { AssetSharedLocationTokenResponse } from '@key-telematics/fleet-api-client';
import { FormBuilderDefinition, KeyFormBuilderComponent } from 'app/shared/components/form-builder';
import { MapToolsService } from 'app/shared/components/map/map-tools.service';
import * as moment from 'moment-timezone';
import { KuiSnackbarService } from 'app/key-ui/snackbar/snackbar.service';
import { cloneDeep } from 'lodash';
import * as _ from 'lodash';

export interface SharingModalOptions {
    share: AssetSharedLocationTokenResponse;
    asset: { id: string; name: string };
}

@Component({
    templateUrl: 'share-modal.component.html',
    // styleUrls: ['report-select-modal.component.scss'],
})
export class SharingModalComponent extends KuiModalRefComponent<SharingModalOptions> implements OnInit {

    modalActions: IKuiModalAction[];

    title: string;
    form: FormBuilderDefinition;
    values: any = {};
    link: string;
    errorMessage: string;

    @ViewChild(KeyFormBuilderComponent, { static: true }) formBuilder: KeyFormBuilderComponent;

    constructor(
        public app: AppService,
        private mapTools: MapToolsService,
        private snackbar: KuiSnackbarService,
        private modalService: ModalService,
        private i18n: TranslateService
    ) {
        super();

    }

    static open(modal: ModalService, options: SharingModalOptions): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            try {
                modal.open<SharingModalOptions>(SharingModalComponent, {
                    data: options,
                    actions: {
                        close: () => {
                            modal.close();
                            resolve(null);
                        },
                        apply: (result: any) => {
                            modal.close();
                            resolve(result);
                        },
                    },

                });
            } catch (err) {
                reject(err);
            }
        });

    }


    ngOnInit() {

        const readonly = !!this.data.share;

        if (readonly) {
            this.modalActions = [{
                text: this.i18n.instant('DIALOG.DELETE'),
                style: 'secondary',
                action: () => {
                    this.modalService.close();
                    this.deleteShare().then(() => {
                        this.actions.close(false);
                    });
                },
            }, {
                text: this.i18n.instant('DIALOG.CLOSE'),
                style: 'primary',
                keypress: 13,
                action: () => {
                    this.actions.close(false);
                },
            }];
        } else {

            this.modalActions = [{
                text: this.i18n.instant('DIALOG.CANCEL'),
                style: 'secondary',
                keypress: 27,
                action: () => {
                    this.actions.close(false);
                },
            }, {
                text: this.i18n.instant('DIALOG.OK'),
                style: 'primary',
                keypress: 13,
                action: () => {
                    this.saveShare();
                    return true;
                },
            }];
        }


        const translate = (key, options?) => this.i18n.instant(`STATUS.DETAILS.SHARE.MODAL.${key}`, options);
        this.form = {
            groups: [{
                name: translate('TITLE'),
                description: translate('DESCRIPTION'),
                fields: [
                    { id: 'name', type: 'text', title: translate('FIELDS.NAME'), required: true, readonly },
                    !readonly && {
                        id: 'expiry', title: translate('FIELDS.EXPIRES_IN'), type: 'combo', readonly, values: [
                            { key: '1', value: translate('FIELDS.1HOUR') },
                            { key: '2', value: translate('FIELDS.HOURS', { hours: 2 }) },
                            { key: '3', value: translate('FIELDS.HOURS', { hours: 3 }) },
                            { key: '6', value: translate('FIELDS.HOURS', { hours: 6 }) },
                            { key: '12', value: translate('FIELDS.HOURS', { hours: 12 }) },
                            { key: '24', value: translate('FIELDS.HOURS', { hours: 24 }) },
                            {
                                key: 'custom', value: translate('FIELDS.EXACT'),
                                fields: [
                                    { id: 'exact', title: translate('FIELDS.EXPIRES_AT'), type: 'datetime', width: 150, required: true, readonly },
                                ],
                            },
                        ],
                    },
                    readonly && { id: 'exact', title: translate('FIELDS.EXPIRES_AT'), type: 'datetime', width: 150, required: true, readonly },
                    {
                        type: 'searchselect', id: 'destination', title: translate('FIELDS.DESTINATION'), max: 1, readonly,
                        placeholder: translate('FIELDS.DESTINATION_PLACEHOLDER'),
                        options: {
                            searchItemPromise: async _id => {
                                return this.values.destination;
                            },
                            searchListPromise: async (_limit, filter: string) => {
                                const action = _marker => { };
                                const searchResults = await this.mapTools.doSearch(decodeURIComponent(filter), action);
                                return { items: (searchResults || []).map(x => ({ id: x.id, name: x.value, filter: x.meta })) };
                            },
                        },
                    },
                ].filter(x => x),
            }],
        };
        this.title = this.form.groups[0].name;
        this.form.groups[0].name = null;


        if (this.data.share) {
            this.values = {
                name: this.data.share.name,
                expiry: 'custom',
                exact: this.data.share.expiry,
                destination: this.data.share.destination ? {
                    id: '1',
                    name: this.data.share.destination?.address,
                    filter: this.data.share.destination,
                } : null,
            };


            
            this.link = window.location.origin + '/#/shared/location/' + this.data.share.token;
            if (this.app.isStaging) {
                this.link += '?e=' + btoa(this.app.api.endpoint);
            }
            
        } else {
            this.values = {
                name: this.data.asset.name,
                expiry: '1',
                exact: moment().toISOString(),
                destination: null,
            };
        }


    }

    detachModal() {
        this.actions.close(false);
    }

    async saveShare() {
        this.errorMessage = '';
        this.link = '';
        if (this.formBuilder.validate()) {

            const result = cloneDeep(this.values);
            
            if (result.destination && result.destination.filter) {
                result.destination = result.destination.filter;
            }

            try {
                let expiryDate = moment();
                if (result.expiry === 'custom') {
                    expiryDate = moment.utc(result.exact).tz(moment.defaultZone.name);
                } else {
                    expiryDate = moment().add(result.expiry, 'hour').utc();
                }
                const token = await this.app.api.entities.createSharedAssetLocationToken(this.data.asset.id, {
                    name: result.name,
                    expiry: expiryDate.toISOString(),
                    destination: result.destination,
                });

                this.data.share = token;
                this.ngOnInit();

            } catch (err) {
                console.error(err);
                this.errorMessage = this.i18n.instant('ERROR.UNEXPECTED_ERROR_OCCURRED');
            }
           
        }
    }


    doCopyLink() {
        // create non visible textarea to copy text from
        const el = document.createElement('textarea');
        el.value = this.link;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);

        // copy text
        const selected =
            document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false;
        el.select();
        document.execCommand('copy');

        // clean up after yourself
        document.body.removeChild(el);
        if (selected) {
            document.getSelection().removeAllRanges();
            document.getSelection().addRange(selected);
        }

        // inform user of action
        this.snackbar.message(this.i18n.instant('STATUS.DETAILS.SHARE.LINK_COPIED'));

        this.detachModal();
    }


    async deleteShare() {

        return this.modalService.confirm(this.i18n.instant('STATUS.DETAILS.SHARE.LINK_DELETE_CONFIRM')).then(async result => {
            if (result) {
                try {
                    await this.app.api.entities.deleteSharedAssetLocationToken(this.data.share.token);
                    this.snackbar.message(this.i18n.instant('STATUS.DETAILS.SHARE.LINK_DELETED'));
                } catch (err) {
                    console.error(err);
                    this.modalService.error(this.i18n.instant('ERROR.UNEXPECTED_ERROR_OCCURRED'), err);
                }
            }
        });
    }

}
